import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  position?: 'right' | 'left';
  width?: string;
  children: React.ReactNode;
  title?: string;
  contentClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  bodyClassName?: string;
};

export const Drawer = ({
  isOpen,
  onClose,
  position = 'right',
  width = '30rem',
  children,
  title,
  contentClassName,
  headerClassName = 'flex items-center justify-between border-b border-neutralExtraLight bg-white px-4 py-4 md:px-6 md:py-6',
  titleClassName = 'text-lg font-semibold text-fontColor md:text-xl lg:text-2xl',
  bodyClassName = 'flex-1 overflow-y-auto p-4 lg:p-6',
}: DrawerProps) => {
  const { t } = useTranslation(['common']);

  const panelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(isOpen); // mounted?
  const [onScreen, setOnScreen] = useState(false); // translate state

  // Mount/unmount timing
  useEffect(() => {
    if (isOpen) {
      setVisible(true); // mount
    } else {
      setOnScreen(false); // slide out
      const tid = setTimeout(() => setVisible(false), 700); // match duration (700ms)
      return () => clearTimeout(tid);
    }
  }, [isOpen]);

  // Force the browser to paint off-screen, then slide in
  useLayoutEffect(() => {
    if (visible && isOpen) {
      setOnScreen(false); // ensure off-screen first
      // Force reflow so the browser commits off-screen position
      // then flip to on-screen in next frame
      const raf = requestAnimationFrame(() => {
        // read to force layout (any layout read works)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        panelRef.current?.offsetWidth;
        setOnScreen(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [visible, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!visible) return null;

  const off = position === 'right' ? 'translate-x-full' : '-translate-x-full';
  const on = 'translate-x-0';

  const drawerContent = (
    <>
      {/* Backdrop (fade) */}
      <div
        className={`fixed inset-0 z-[998] bg-black transition-opacity duration-700 ease-in-out ${
          isOpen ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel (slide) - Responsive: full-screen mobile, partial tablet, custom desktop */}
      <div
        ref={panelRef}
        className={`fixed top-0 z-[999] h-full w-full transform-gpu bg-white shadow-xl transition-transform duration-700 ease-in-out will-change-transform md:w-auto ${position === 'right' ? 'right-0' : 'left-0'} ${onScreen ? on : off}`}
        style={{
          width: typeof window !== 'undefined' && window.innerWidth >= 768 ? width : '100%',
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          {/* Header - Responsive padding and text sizing */}
          <div className={headerClassName}>
            <h2 className={titleClassName}>{title || 'Drawer'}</h2>
            {/* Close button - Touch-friendly on mobile/tablet */}
            <button
              onClick={onClose}
              type="button"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-1 text-neutralMedium transition-all duration-300 hover:scale-110 hover:bg-neutralExtraLight hover:text-neutralDark lg:min-h-0 lg:min-w-0"
            >
              <span className="sr-only">{t('close')}</span>
              <svg
                className="h-6 w-6 lg:h-5 lg:w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {/* Content - Responsive padding: 16px mobile, 16px tablet, 24px desktop */}
          <div className={`${bodyClassName} ${contentClassName ?? ''}`}>{children}</div>
        </div>
      </div>
    </>
  );

  // Render using Portal to avoid stacking context issues
  if (typeof document === 'undefined') return null;
  return createPortal(drawerContent, document.body);
};
